import React, { useEffect, useState } from "react"
import { Box } from "@mui/material"
import PropTypes from "prop-types"
import { ReactComponent as TableBaseIcon } from "src/v2/assets/icons/table-base-icon.svg"
import ButtonWithIcon from "src/v2/components/Button/Button"
import { BoxDataCompany } from "../../components/box-data-company/box-data-company"
import CompanySummarySections from "./company-summary-sections"
import { useCompanySummaryData } from "./hooks/use-company-summary-data"
import { getCnpjRoot } from "src/v2/utils/cnpj-root"
import { ADMIN_COMPANY_ACTIONS } from "./constants"
import CompanyRegistrationModal from "./registrations/company-registration-modal"
import CompanyHistoryModal from "./history/company-history-modal"
import CompanyFinancialModal from "./financial/company-financial-modal"
import { useCompanyFinancialData } from "./financial/use-company-financial-data"

const CompanyTab = ({ companyData, isAdminUser, onCompanyDataChange }) => {
    const [activeModal, setActiveModal] = useState(null)
    const [refreshKey, setRefreshKey] = useState(0)
    const hasCompanyIdentifier = Boolean(companyData?.CPFCNPJ)
    const financialRootCnpj = companyData?.RaizCNPJ ?? getCnpjRoot(companyData?.CPFCNPJ)
    const financial = useCompanyFinancialData({ raizCnpj: financialRootCnpj })
    const summaries = useCompanySummaryData({
        company: companyData,
        refreshKey,
    })

    const handleCloseModal = () => {
        setActiveModal(null)
    }

    const isRegistrationModal = ADMIN_COMPANY_ACTIONS.includes(activeModal)

    const handleCounterpartSaved = () => {
        setRefreshKey((currentKey) => currentKey + 1)
        onCompanyDataChange()
    }

    useEffect(() => {
        setActiveModal(null)
    }, [companyData?.CPFCNPJ])

    return (
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 3, p: 3 }}>
            <BoxDataCompany companyData={companyData} />

            {isAdminUser && (
                <Box display="flex" flexWrap="wrap" gap={{ xxxs: 2, md: 3 }}>
                    {ADMIN_COMPANY_ACTIONS.map((label) => (
                        <ButtonWithIcon
                            key={label}
                            icon={<TableBaseIcon />}
                            disabled={!hasCompanyIdentifier}
                            onClick={() => setActiveModal(label)}
                        >
                            {label}
                        </ButtonWithIcon>
                    ))}
                </Box>
            )}

            <CompanySummarySections summaries={summaries} financial={financial} onOpenDetails={setActiveModal} />

            {activeModal === "financial" ? (
                <CompanyFinancialModal
                    open
                    onClose={handleCloseModal}
                    financial={financial}
                    companyData={companyData}
                />
            ) : isRegistrationModal ? (
                <CompanyRegistrationModal
                    open
                    registrationType={activeModal}
                    companyIdentifier={companyData?.CPFCNPJ}
                    onClose={handleCloseModal}
                    onContraparteSaved={handleCounterpartSaved}
                />
            ) : activeModal ? (
                <CompanyHistoryModal
                    open
                    historyType={activeModal}
                    company={companyData}
                    isAdminUser={isAdminUser}
                    onClose={handleCloseModal}
                    onChanged={() => setRefreshKey((currentKey) => currentKey + 1)}
                />
            ) : null}
        </Box>
    )
}

CompanyTab.propTypes = {
    companyData: PropTypes.object,
    isAdminUser: PropTypes.bool,
    onCompanyDataChange: PropTypes.func,
}

CompanyTab.defaultProps = {
    companyData: {},
    isAdminUser: false,
    onCompanyDataChange: () => {},
}

export default CompanyTab
